import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Printer, ArrowLeft, Plus, X, Landmark } from "lucide-react";
import axios from "axios";
import { FIRM, firmContactLine } from "@/lib/firm";
import { numberToWords as numWords } from "@/lib/format";

const API = process.env.REACT_APP_BACKEND_URL;

const todayISO = () => new Date().toISOString().slice(0, 10);
const plusDaysISO = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const fmtLongDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }); }
  catch { return iso; }
};
const fmtMoney = (n) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const amountWords = (n) => {
  const dollars = Math.floor(Number(n || 0));
  const words = numWords(dollars).replace(/ Dollars.*$/, "").trim() || "Zero";
  return `${words} U.S. Dollars`;
};

const DEFAULT_CONDITIONS = [
  "Receipt and satisfactory review of a current, itemized payoff statement from the existing lender/servicer, good through the funding date.",
  "Clear, marketable title confirmed by a current title commitment; the funded lien to be first-priority upon closing.",
  "Satisfactory verification of the borrower/obligor's identity (KYC/CIP) and authority to transact.",
  "Execution of the loan/security documents and recordation of the security instrument.",
  "No material adverse change in the collateral or the borrower's circumstances prior to funding.",
];

const Ctl = ({ label, value, onChange, type = "text", full = false, area = false, placeholder = "" }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <label className="block text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A] mb-1">{label}</label>
    {area ? (
      <textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:outline-none focus:border-[#B8860B]" />
    ) : (
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:outline-none focus:border-[#B8860B]" />
    )}
  </div>
);

const ItemList = ({ label, items, setItems, testid }) => (
  <div className="md:col-span-2" data-testid={testid}>
    <label className="block text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A] mb-1">{label}</label>
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className="flex items-start gap-2">
          <span className="text-[#8A8A82] text-[12px] pt-2 w-4 text-right">{i + 1}.</span>
          <textarea rows={2} value={it} data-testid={`${testid}-item-${i}`}
            onChange={(e) => setItems(items.map((x, j) => (j === i ? e.target.value : x)))}
            className="flex-1 bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:outline-none focus:border-[#B8860B]" />
          <button onClick={() => setItems(items.filter((_, j) => j !== i))} data-testid={`${testid}-remove-${i}`}
            title="Remove" className="mt-1 text-[#9A9A90] hover:text-[#E4646E] p-1"><X size={14} /></button>
        </div>
      ))}
    </div>
    <button onClick={() => setItems([...items, ""])} data-testid={`${testid}-add`}
      className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-[#E4C97A] hover:text-[#d4a017]">
      <Plus size={13} /> Add condition
    </button>
  </div>
);

export default function FundingCommitment() {
  const { appId } = useParams();
  const [f, setF] = useState({
    date: todayISO(),
    expiry: plusDaysISO(30),
    commitmentNo: "",
    funderName: FIRM.name,
    funderAddress: FIRM.addressLines.join("\n"),
    borrowerName: "",
    borrowerAddress: "",
    propertyAddress: "",
    purpose: "Refinance / payoff of the existing mortgage loan",
    facilityAmount: 0,
    rate: "",
    termMonths: "",
    existingLender: "",
    payoffAmount: 0,
    originationFeePct: "1.00",
    closingTarget: plusDaysISO(21),
    signerName: FIRM.officer || "",
    signerTitle: "Authorized Officer",
  });
  const [conditions, setConditions] = useState(DEFAULT_CONDITIONS);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!appId) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/borrower/applications/${appId}`);
        const app = r.data.application || {};
        const b = app.binder || {};
        const short = String(app.id || "").replace(/-/g, "").slice(0, 8).toUpperCase();
        const yr = (b.closingDate || todayISO()).slice(0, 4);
        setF((p) => ({
          ...p,
          commitmentNo: p.commitmentNo || `STF-FC-${yr}-${short}`,
          borrowerName: b.borrowerName || p.borrowerName,
          borrowerAddress: b.borrowerAddress || p.borrowerAddress,
          propertyAddress: b.propertyAddress || b.borrowerAddress || p.propertyAddress,
          facilityAmount: Number(b.newPrincipal || b.payoffAmount || 0) || p.facilityAmount,
          rate: (b.newRate != null && b.newRate !== "") ? String(b.newRate) : p.rate,
          termMonths: b.newTermMonths ? String(b.newTermMonths) : p.termMonths,
          existingLender: b.currentLender || p.existingLender,
          payoffAmount: Number(b.payoffAmount || 0) || p.payoffAmount,
        }));
      } catch { /* manual entry */ }
    })();
  }, [appId]);

  const feeAmt = (Number(f.facilityAmount || 0) * (Number(f.originationFeePct || 0) / 100));

  return (
    <div className="binder-print-root min-h-screen bg-[#1a1a14] text-[#ECECE4]">
      <div className="no-print sticky top-0 z-20 bg-[#13130E] border-b border-[#2A2A22]">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-[#9A9A90] hover:text-white flex items-center gap-1.5 text-[12px]" data-testid="fc-back-link">
              <ArrowLeft size={14} /> Back
            </Link>
            <Link to="/" className="text-[#9A9A90] hover:text-white text-[12px]" data-testid="fc-home-link">Home</Link>
            <div className="flex items-center gap-2">
              <Landmark size={16} className="text-[#B8860B]" />
              <span className="font-serif text-lg">Funding Commitment · Term Sheet</span>
            </div>
          </div>
          <button onClick={() => window.print()} data-testid="fc-print-btn"
            className="flex items-center gap-2 bg-[#B8860B] hover:bg-[#a0750a] text-[#13130E] font-semibold text-[12px] uppercase tracking-[0.12em] px-4 py-2 rounded-md">
            <Printer size={15} /> Print / Save as PDF
          </button>
        </div>
      </div>

      <div className="no-print bg-[#13130E] border-b border-[#2A2A22]">
        <div className="max-w-5xl mx-auto px-6 py-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="fc-controls">
          <Ctl label="Commitment Date" type="date" value={f.date} onChange={set("date")} />
          <Ctl label="Valid Through" type="date" value={f.expiry} onChange={set("expiry")} />
          <Ctl label="Commitment No." value={f.commitmentNo} onChange={set("commitmentNo")} />
          <Ctl label="Target Closing Date" type="date" value={f.closingTarget} onChange={set("closingTarget")} />

          <Ctl label="Funder / Lender Name" value={f.funderName} onChange={set("funderName")} full />
          <Ctl label="Funder Address" value={f.funderAddress} onChange={set("funderAddress")} area full />

          <Ctl label="Borrower / Obligor Name" value={f.borrowerName} onChange={set("borrowerName")} full />
          <Ctl label="Borrower Address" value={f.borrowerAddress} onChange={set("borrowerAddress")} area full />

          <Ctl label="Property / Collateral Address" value={f.propertyAddress} onChange={set("propertyAddress")} full />
          <Ctl label="Purpose" value={f.purpose} onChange={set("purpose")} full />

          <Ctl label="Facility Amount (USD)" type="number" value={f.facilityAmount} onChange={(v) => set("facilityAmount")(Number(v))} />
          <Ctl label="Interest Rate (% / yr)" value={f.rate} onChange={set("rate")} />
          <Ctl label="Term (months)" value={f.termMonths} onChange={set("termMonths")} />
          <Ctl label="Origination Fee (%)" value={f.originationFeePct} onChange={set("originationFeePct")} />

          <Ctl label="Existing Lender (payoff target)" value={f.existingLender} onChange={set("existingLender")} full />
          <Ctl label="Existing Payoff Amount (USD)" type="number" value={f.payoffAmount} onChange={(v) => set("payoffAmount")(Number(v))} />

          <Ctl label="Signer Name" value={f.signerName} onChange={set("signerName")} />
          <Ctl label="Signer Title" value={f.signerTitle} onChange={set("signerTitle")} />

          <ItemList label="Conditions Precedent to Funding" items={conditions} setItems={setConditions} testid="fc-conditions" />
        </div>
      </div>

      <div className="print-area max-w-5xl mx-auto px-6 py-8 flex flex-col items-center gap-8">
        <div className="paper-sheet legal-page" data-testid="fc-letter">
          <div className="fc-head">
            <div className="fc-firm">{f.funderName || FIRM.name}</div>
            <div className="fc-firm-sub">{firmContactLine}</div>
          </div>
          <div className="lg-rule" />

          <div className="fc-eyebrow">Funding Commitment &amp; Conditional Term Sheet</div>
          <div className="fc-meta">
            <div><strong>Date:</strong> {fmtLongDate(f.date)}</div>
            <div><strong>Commitment No.:</strong> {f.commitmentNo || "—"}</div>
            <div><strong>Valid Through:</strong> {fmtLongDate(f.expiry)}</div>
          </div>

          <div className="fc-block">
            <div className="fc-lbl">To (Borrower / Obligor):</div>
            <div><strong>{f.borrowerName || "____________________"}</strong></div>
            {String(f.borrowerAddress || "").split("\n").filter(Boolean).map((l, i) => <div key={i}>{l}</div>)}
          </div>

          <p style={{ marginTop: 14 }}>
            Subject to the conditions set forth below, <strong>{f.funderName || FIRM.name}</strong> (the "Funder") is pleased to
            offer this conditional commitment to fund the transaction described below. This commitment is a proposal of terms;
            it is not itself a loan, an advance of funds, or a payment instrument, and it becomes binding only upon the Borrower's
            written acceptance and the satisfaction of all conditions precedent.
          </p>

          <div className="lg-h">Summary of Terms</div>
          <table className="lg-table"><tbody>
            <tr><td>Borrower / Obligor</td><td>{f.borrowerName || "—"}</td></tr>
            <tr><td>Property / Collateral</td><td>{f.propertyAddress || "—"}</td></tr>
            <tr><td>Purpose</td><td>{f.purpose || "—"}</td></tr>
            <tr><td>Facility Amount</td><td>USD {fmtMoney(f.facilityAmount)} ({amountWords(f.facilityAmount)})</td></tr>
            <tr><td>Interest Rate</td><td>{f.rate ? `${f.rate}% per annum` : "—"}</td></tr>
            <tr><td>Term</td><td>{f.termMonths ? `${f.termMonths} months` : "—"}</td></tr>
            <tr><td>Origination Fee</td><td>{f.originationFeePct ? `${f.originationFeePct}% (USD ${fmtMoney(feeAmt)})` : "—"}</td></tr>
            <tr><td>Existing Lender (to be paid off)</td><td>{f.existingLender || "—"}</td></tr>
            <tr><td>Estimated Payoff to Existing Lender</td><td>USD {fmtMoney(f.payoffAmount)}</td></tr>
            <tr><td>Target Closing / Funding Date</td><td>{fmtLongDate(f.closingTarget)}</td></tr>
          </tbody></table>

          <div className="lg-h">Conditions Precedent to Funding</div>
          <p>Funding is expressly conditioned upon satisfaction (or written waiver by the Funder) of each of the following:</p>
          <ol className="lg-ol" data-testid="fc-conditions-print">
            {conditions.filter((c) => c && c.trim()).map((c, i) => <li key={i}>{c}</li>)}
          </ol>

          <div className="lg-h">General Provisions</div>
          <ol className="lg-ol">
            <li><strong>Nature of Commitment.</strong> This is a conditional commitment only. No funds are advanced, and no obligation of the existing lender is satisfied, unless and until the Funder actually disburses the facility amount at closing to the designated payoff account.</li>
            <li><strong>Source of Funds.</strong> Funding will be made in immediately available funds (wire) from the Funder's own capital or committed funding source. This commitment does not authorize presentment of any draft or instrument to any party as payment.</li>
            <li><strong>Expiration.</strong> This commitment expires on {fmtLongDate(f.expiry)} if not accepted in writing, and terminates if closing has not occurred by {fmtLongDate(f.closingTarget)} (or such later date as the Funder agrees in writing).</li>
            <li><strong>No Third-Party Reliance.</strong> This commitment runs solely between the Funder and the Borrower and confers no rights on any third party.</li>
          </ol>

          <div className="lg-sig-grid">
            <div>
              <div className="lg-sig-party">Funder</div>
              <div className="lg-sig-role">{f.funderName || FIRM.name}</div>
              <div className="lg-sig-rule" />
              <div className="lg-sig-cap">{f.signerName || "____________________"}, {f.signerTitle}</div>
              <div className="lg-sig-cap">Date: {fmtLongDate(f.date)}</div>
            </div>
            <div>
              <div className="lg-sig-party">Accepted — Borrower / Obligor</div>
              <div className="lg-sig-role">{f.borrowerName || "____________________"}</div>
              <div className="lg-sig-rule" />
              <div className="lg-sig-cap">Signature</div>
              <div className="lg-sig-cap">Date: __________________</div>
            </div>
          </div>

          <p className="lg-note">Template for review by licensed counsel before issuance. This document is a conditional proposal of terms, not a loan, a commitment to lend absent the stated conditions, or a payment/negotiable instrument. Nothing herein constitutes legal or financial advice.</p>
        </div>
      </div>

      <style>{`
        @media print {
          html, body { background:#fff !important; }
          .binder-print-root { background:#fff !important; color:#000 !important; min-height:0 !important; }
          .print-area { max-width:none !important; padding:0 !important; margin:0 !important; }
          .paper-sheet { box-shadow:none !important; margin:0 !important; page-break-after:auto; }
          .legal-page { padding:0.7in 0.8in !important; }
          .lg-ol > li, p, .lg-sig-grid { break-inside:avoid !important; page-break-inside:avoid !important; }
          .lg-table, tr { break-inside:avoid !important; page-break-inside:avoid !important; }
        }
        .paper-sheet { background:#fff; color:#0A0A0A; box-shadow:0 10px 40px rgba(0,0,0,.4); margin:0 auto; }
        .legal-page { width:8.5in; min-height:11in; padding:0.7in 0.8in; font-family:Georgia,'Times New Roman',serif; font-size:11pt; line-height:1.5; color:#111; }
        .fc-head { text-align:center; }
        .fc-firm { font-size:19pt; font-weight:700; letter-spacing:.18em; color:#0B4636; }
        .fc-firm-sub { font-size:9pt; color:#444; margin-top:3px; }
        .lg-rule { height:2px; background:#0B4636; margin:12px 0 16px; }
        .fc-eyebrow { font-size:9pt; letter-spacing:.16em; text-transform:uppercase; color:#0B4636; font-weight:700; }
        .fc-meta { font-size:10.5pt; line-height:1.6; margin-top:6px; }
        .fc-block { margin-top:14px; font-size:10.5pt; line-height:1.4; }
        .fc-lbl { font-weight:700; margin-bottom:3px; }
        .lg-h { font-weight:700; letter-spacing:.04em; margin:16px 0 6px; color:#0B4636; }
        .lg-ol { padding-left:20px; list-style:decimal outside; }
        .lg-ol > li { margin-bottom:8px; display:list-item; }
        .lg-note { font-size:9.5pt; font-style:italic; color:#555; margin-top:22px; border-top:1px solid #ddd; padding-top:10px; }
        .lg-table { width:100%; border-collapse:collapse; margin-top:8px; font-size:10.5pt; }
        .lg-table td { border:1px solid #d8d8d0; padding:6px 9px; }
        .lg-table td:first-child { background:#f6f6f1; font-weight:600; width:46%; }
        .lg-sig-grid { display:grid; grid-template-columns:1fr 1fr; gap:40px; margin-top:28px; }
        .lg-sig-party { font-weight:700; text-transform:uppercase; font-size:10pt; letter-spacing:.04em; }
        .lg-sig-role { font-size:9.5pt; color:#555; margin-bottom:26px; }
        .lg-sig-rule { border-bottom:1.2px solid #111; margin:2px 0 6px; }
        .lg-sig-cap { font-size:10pt; }
      `}</style>
    </div>
  );
}
